import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Database, ShieldCheck } from '@phosphor-icons/react';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';

/**
 * Public Privacy Policy. Covers the MigrationCrunch landing surfaces AND the
 * SalesCrunchCRM core product (shared infrastructure).
 *
 * Last reviewed: Feb 2026. If material changes are made (new sub-processor,
 * new data category, new retention period), the "Last updated" date below
 * MUST be bumped — that is how visitors and regulators know the policy changed.
 *
 * This is plain-language, not lawyer-speak. Designed to be readable in
 * 5 minutes by a real human. For an enterprise audit or a DPA, the user
 * should send the inquiry to privacy@migrationcrunch.com.
 */

const LAST_UPDATED = 'February 16, 2026';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Privacy Policy — MigrationCrunch & SalesCrunchCRM';
    let metaDesc = document.querySelector('meta[name="description"]');
    let createdMeta = false;
    let prevDesc = null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
      createdMeta = true;
    } else {
      prevDesc = metaDesc.getAttribute('content');
    }
    metaDesc.setAttribute(
      'content',
      'How MigrationCrunch and SalesCrunchCRM collect, use, store, and protect your data. Plain language. No tricks.'
    );
    return () => {
      document.title = prevTitle;
      if (createdMeta) metaDesc.remove();
      else if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white" data-testid="privacy-policy-page">
      {/* Shared nav header */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">
              Migration<span className="text-[#F39C12]">Crunch</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/migrate" className="text-white/70 hover:text-amber-300 transition-colors">Home</Link>
            <Link to="/migrate/compare" className="text-white/70 hover:text-amber-300 transition-colors">Compare</Link>
            <Link to="/contact-us" className="text-white/70 hover:text-amber-300 transition-colors">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 text-amber-300 text-[10px] font-black uppercase tracking-widest mb-3">
          <ShieldCheck size={14} weight="fill" />
          Legal
        </div>
        <h1 className="text-4xl md:text-5xl font-black mb-2 leading-tight">Privacy Policy</h1>
        <p className="text-sm text-white/50 mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <p className="text-base text-white/80 leading-relaxed mb-8">
          This policy explains, in plain English, what data MigrationCrunch and
          SalesCrunchCRM (the &quot;Service&quot;) collect, how we use it, and the choices you
          have. If anything here is unclear, email{' '}
          <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:privacy@migrationcrunch.com">
            privacy@migrationcrunch.com
          </a>{' '}
          and a human will reply.
        </p>

        <Section title="1. What we collect">
          <p>
            We collect only what we need to run the product:
          </p>
          <ul className="space-y-2 mt-3 mb-3">
            <Bullet>
              <strong>Account data:</strong> your name, email address, and password
              (hashed — we never see it in cleartext).
            </Bullet>
            <Bullet>
              <strong>CRM data you import:</strong> contacts, notes, activities, and
              other records you upload or sync from a third-party CRM. This is your
              business data — we treat it that way.
            </Bullet>
            <Bullet>
              <strong>Payment data:</strong> handled by Stripe. We see the last 4
              digits of your card and a transaction ID, never the full number.
            </Bullet>
            <Bullet>
              <strong>Product usage:</strong> basic logs (which pages you visit, which
              features you use) so we can fix bugs and improve the product. No fingerprinting.
            </Bullet>
            <Bullet>
              <strong>Support emails:</strong> if you email us, we keep the email
              thread for support history.
            </Bullet>
          </ul>
          <p>
            We do <em className="text-white">not</em> collect: location data,
            biometric data, advertising IDs, or social-network connections.
          </p>
        </Section>

        <Section title="2. How we use it">
          <p>
            Strictly to operate the Service:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>Authenticate your login.</Bullet>
            <Bullet>Run migrations and AI note re-association on data you upload.</Bullet>
            <Bullet>Charge your card (via Stripe) for paid tiers.</Bullet>
            <Bullet>Send transactional emails (purchase confirmations, password resets, migration completion).</Bullet>
            <Bullet>Improve the product based on aggregated, anonymized usage patterns.</Bullet>
            <Bullet>Respond to your support questions.</Bullet>
          </ul>
          <p className="mt-3">
            We do <em className="text-white">not</em> sell your data. We do not run
            ads on it. We do not train external AI models on it.
          </p>
        </Section>

        <Section title="3. Third parties we use (sub-processors)">
          <p>
            To deliver the Service we share narrow slices of data with these vendors,
            each of which has their own privacy commitments:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>
              <strong>Stripe</strong> — payment processing. They see your card details. We don&apos;t.
            </Bullet>
            <Bullet>
              <strong>OpenAI / Anthropic / Google</strong> (via Emergent LLM gateway) —
              processes the AI features (note extraction, Crunchy assistant). Data sent
              to these vendors is used only to produce a response for your request and
              is not used to train their public models per their enterprise terms.
            </Bullet>
            <Bullet>
              <strong>Resend</strong> — sends transactional emails (receipts, alerts).
            </Bullet>
            <Bullet>
              <strong>Emergent (hosting)</strong> — the platform that runs our app
              infrastructure.
            </Bullet>
            <Bullet>
              <strong>MongoDB Atlas</strong> — our database provider, encrypted at rest.
            </Bullet>
            <Bullet>
              <strong>Rewardful</strong> (dormant) — affiliate tracking. Only activates
              if you arrive via an affiliate link. Currently inactive.
            </Bullet>
          </ul>
        </Section>

        <Section title="4. Where we store it">
          <p>
            Data is stored in encrypted MongoDB clusters hosted in the United States.
            Payment data is held by Stripe in their PCI-compliant systems. Email
            messages are held by Resend.
          </p>
        </Section>

        <Section title="5. How long we keep it">
          <ul className="space-y-2 mt-3">
            <Bullet>
              <strong>Account + CRM data:</strong> as long as your account is active,
              plus 30 days after you delete it (to handle accidental deletes and final invoices).
            </Bullet>
            <Bullet>
              <strong>Payment records:</strong> 7 years (US tax + accounting requirements).
            </Bullet>
            <Bullet>
              <strong>Server logs:</strong> 90 days.
            </Bullet>
            <Bullet>
              <strong>AI inputs sent to LLM vendors:</strong> not stored by us after
              the response is returned; subject to each vendor&apos;s own retention windows
              (typically 30 days or less).
            </Bullet>
          </ul>
        </Section>

        <Section title="6. Your rights">
          <p>
            Wherever you live, you can:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet><strong>Export</strong> your data — request a copy any time.</Bullet>
            <Bullet><strong>Delete</strong> your account and all associated data.</Bullet>
            <Bullet><strong>Correct</strong> any inaccurate data we hold about you.</Bullet>
            <Bullet><strong>Object</strong> to processing or restrict it.</Bullet>
            <Bullet><strong>Opt out</strong> of non-essential cookies (the cookie banner on every page lets you choose).</Bullet>
          </ul>
          <p className="mt-3">
            To exercise any of these, email{' '}
            <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:privacy@migrationcrunch.com">
              privacy@migrationcrunch.com
            </a>{' '}
            with the subject line &quot;Privacy request&quot;. We aim to respond within 7 days, never more than 30.
          </p>
          <p className="mt-3 text-sm text-white/60">
            <strong className="text-white/80">EU / UK residents:</strong> you also have
            the right to lodge a complaint with your local data protection authority.
            <br />
            <strong className="text-white/80">California residents:</strong> you have
            rights under the CCPA/CPRA, including the right to know what we collect
            and to opt out of any &quot;sale&quot; of personal information (we don&apos;t sell any).
          </p>
        </Section>

        <Section title="7. Cookies">
          <p>
            We use a minimal set of cookies:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>
              <strong>Essential:</strong> keep you logged in, remember your preferences.
              Cannot be disabled while using the app.
            </Bullet>
            <Bullet>
              <strong>Analytics (optional):</strong> anonymous usage data so we can fix
              bugs. The cookie banner on every page lets you decline these.
            </Bullet>
          </ul>
          <p className="mt-3">No advertising cookies. No cross-site trackers.</p>
        </Section>

        <Section title="8. Security">
          <p>
            We use HTTPS everywhere, bcrypt for password hashing, encryption at rest in
            the database, and time-limited JWT tokens for authentication. If we discover
            a security incident that affects your data, we will notify you by email
            within 72 hours per GDPR requirements.
          </p>
        </Section>

        <Section title="9. Children">
          <p>
            The Service is not for anyone under 16. We don&apos;t knowingly collect data
            from children. If you believe a child has signed up, email us and we&apos;ll
            delete the account.
          </p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            If we materially change this policy, we will update the &quot;Last updated&quot;
            date at the top of the page and, where required, email account holders
            before the change takes effect.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            <strong className="text-white">Privacy questions:</strong>{' '}
            <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:privacy@migrationcrunch.com">
              privacy@migrationcrunch.com
            </a>
          </p>
          <p>
            <strong className="text-white">General support:</strong>{' '}
            <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:chip@migrationcrunch.com">
              chip@migrationcrunch.com
            </a>
          </p>
          <p className="mt-3">
            Or use the{' '}
            <Link to="/contact-us" className="text-amber-300 hover:text-amber-200 underline">
              contact form
            </Link>
            .
          </p>
        </Section>
      </main>

      <MigrationCrunchFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`privacy-section-${title.toLowerCase().split(' ').slice(0, 2).join('-')}`}>
      <h2 className="text-xl md:text-2xl font-black mb-3 text-white">{title}</h2>
      <div className="text-sm md:text-base text-white/75 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3">
      <span className="text-amber-400 mt-1.5 flex-shrink-0">▸</span>
      <span>{children}</span>
    </li>
  );
}
